import css from './style.module.css';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function EditClientInformation({ loginToken, editClient, setEditClient, setCompaniesData }) {
  const navigation = useNavigate();
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('');
  const [currentFileUrl, setCurrentFileUrl] = useState('');

  // Separate visibility state for each field
  const [passwordVisibility, setPasswordVisibility] = useState({
    sfUsername: false,
    sfPassword: false,
    sfSecurityToken: false,
    oktaClientID: false
  });

  // Validation schema - only validates if field is being changed
  const validationSchema = Yup.object({
    companyName: Yup.string()
      .min(2, 'Company name must be at least 2 characters')
      .nullable(),
    oktaDomain: Yup.string()
      .test('is-url-if-provided', 'Must be a valid URL', function (value) {
        if (!value || value.trim() === '') return true;
        return Yup.string().url().isValidSync(value);
      })
      .nullable(),
    oktaClientID: Yup.string()
      .test('min-length-if-provided', 'Client ID must be at least 10 characters', function (value) {
        if (!value || value.trim() === '' || /^[•*]+$/.test(value)) return true;
        return value.length >= 10;
      })
      .nullable(),
    sfUsername: Yup.string()
      .test('is-email-if-provided', 'Must be a valid email address', function (value) {
        if (!value || value.trim() === '' || /^[•*]+$/.test(value)) return true;
        return Yup.string().email().isValidSync(value);
      })
      .nullable(),
    sfSecurityToken: Yup.string()
      .test('min-length-if-provided', 'Token must be at least 10 characters', function (value) {
        if (!value || value.trim() === '' || /^[•*]+$/.test(value)) return true;
        return value.length >= 10;
      })
      .nullable(),
    companyDomain: Yup.string()
      .test('is-domain-if-provided', 'Must be a valid domain (e.g., example.com)', function (value) {
        if (!value || value.trim() === '') return true;
        return /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/.test(value);
      })
      .nullable(),
    sfPassword: Yup.string()
      .test('min-length-if-provided', 'Password must be at least 8 characters', function (value) {
        if (!value || value.trim() === '' || /^[•*]+$/.test(value)) return true;
        return value.length >= 8;
      })
      .nullable(),
    senderEmail: Yup.string()
      .test('is-email-if-provided', 'Must be a valid email address', function (value) {
        if (!value || value.trim() === '') return true;
        return Yup.string().email().isValidSync(value);
      })
      .nullable(),
    status: Yup.string()
      .oneOf(['active', 'inactive', ''], 'Invalid status selection')
      .nullable()
  });

  // Initialize current file URL when component mounts
  useEffect(() => {
    if (editClient?.logo) {
      setCurrentFileUrl(editClient.logo);
    }
  }, [editClient]);

  // Toggle visibility for specific field
  const toggleVisibility = (fieldName) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Handle file selection - WEBP only
  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      // Check if file is WEBP
      if (file.type !== 'image/webp') {
        setFileError('Only WEBP image files are allowed');
        setSelectedFile(null);
        event.target.value = ''; // Clear the input
        return;
      }

      // Optional: Check file size (e.g., max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setFileError('File size must be less than 5MB');
        setSelectedFile(null);
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      setFileError('');
      setError('');
    }
  };

  // Upload file to GCS (generates new filename and URL)
  const uploadFileToGCS = async (file, companyName) => {
    try {
      setUploadStage("Getting upload permission...");
      setUploadProgress(10);

      // Get signed URL from backend for NEW file upload
      const signedUrlResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/signed-url`,
        {
          fileName: companyName.replace(/ /g, ""), // New file will get a unique name
          contentType: file.type,
          company: true
        },
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        }
      );

      const { signedUrl, publicUrl } = signedUrlResponse.data;

      // Upload file directly to GCS
      setUploadStage("Uploading file...");
      setUploadProgress(15);

      const xhr = new XMLHttpRequest();

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const fileUploadProgress = Math.round((event.loaded / event.total) * 70);
            setUploadProgress(15 + fileUploadProgress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadProgress(90);
            setUploadStage("File uploaded successfully...");
            resolve({ publicUrl });
          } else {
            reject(new Error(`Upload failed with status: ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', signedUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      return await uploadPromise;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const submitEditClient = async (values, { setSubmitting, resetForm }) => {
    setError('');

    try {
      let logoUrl = currentFileUrl; // Default to current logo

      // If a new file is selected, upload it first
      if (selectedFile) {
        setUploadStage("Starting upload...");
        const uploadResult = await uploadFileToGCS(selectedFile, values.companyName);
        logoUrl = uploadResult.publicUrl; // Get the new logo URL
      }

      // Submit form data with new logo URL and flag to delete old logo
      setUploadStage("Updating client data...");
      setUploadProgress(95);

      // Build payload with required fields
      const payload = {
        companyName: values.companyName,
        companyDomain: values.companyDomain,
        senderEmail: values.senderEmail,
        status: values.status === 'active',
        logo: logoUrl,
        deleteOldLogo: selectedFile ? true : false
      };

      // Only add oktaDomain if it has a value
      if (values.oktaDomain && values.oktaDomain.trim()) {
        payload.oktaDomain = values.oktaDomain;
      }

      // Only include sensitive fields if they've been changed (not masked/empty)
      // Check if the value is not empty, not just dots/bullets, and not the placeholder
      if (values.oktaClientID &&
        values.oktaClientID.trim() &&
        values.oktaClientID !== '••••••••' &&
        !/^[•*]+$/.test(values.oktaClientID)) {
        payload.oktaClientID = values.oktaClientID;
      }

      if (values.sfUsername &&
        values.sfUsername.trim() &&
        values.sfUsername !== '••••••••' &&
        !/^[•*]+$/.test(values.sfUsername)) {
        payload.sfUsername = values.sfUsername;
      }

      if (values.sfPassword &&
        values.sfPassword.trim() &&
        values.sfPassword !== '••••••••' &&
        !/^[•*]+$/.test(values.sfPassword)) {
        payload.sfPassword = values.sfPassword;
      }

      if (values.sfSecurityToken &&
        values.sfSecurityToken.trim() &&
        values.sfSecurityToken !== '••••••••' &&
        !/^[•*]+$/.test(values.sfSecurityToken)) {
        payload.sfSecurityToken = values.sfSecurityToken;
      }

      // console.log("Updating company with ID:", editClient.id);
      // console.log("Payload being sent:", { ...payload, sfPassword: '***', sfUsername: '***', sfSecurityToken: '***', oktaClientID: '***' }); // Log without sensitive data

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/companies/update/${editClient.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${loginToken}`,
          },
        }
      );

      if (response.status !== 200) {
        throw new Error('Failed to update client');
      }

      // Use the response data from backend
      const updatedCompany = {
        ...response.data.companyInfo,
        id: editClient.id // Ensure ID is preserved
      };

      // Update companies data in parent component
      setCompaniesData(prevData =>
        prevData.map(company =>
          company.id === editClient.id ? updatedCompany : company
        )
      );

      setUploadProgress(100);
      setUploadStage("Client updated successfully!");

      // Success - reset and close modal after brief delay
      setTimeout(() => {
        setUploadProgress(0);
        setUploadStage('');
        setSelectedFile(null);
        setFileError('');
        setCurrentFileUrl(''); // Reset current file URL
        resetForm();
        setEditClient(null);
      }, 1500);

    } catch (error) {
      console.error("Error updating client:", error);

      // Handle authentication errors
      if (error?.response?.status === 401) {
        localStorage.removeItem("Firebase Auth");
        localStorage.removeItem("okta-token-storage");
        localStorage.removeItem("okta-cache-storage");
        navigation('/');
        return;
      }

      // Handle other errors
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update client';
      setError(errorMessage);

      setUploadProgress(0);
      setUploadStage('');
    } finally {
      setSubmitting(false);
    }
  };

  // Error message component for consistent styling
  const ErrorMessage = ({ message }) => (
    <div className="text-danger text-start ms-1 mt-1" style={{ fontSize: '0.875rem' }}>
      <i className="fa-solid fa-exclamation-circle me-1"></i>
      {message}
    </div>
  );

  if (!editClient) return null;

  return (
    <section className={css.addNewClientSection}>
      <div className={css.addNewClientContainer}>
        <div className='d-flex justify-content-between align-items-center mb-3'>
          <h4>Edit Client Information</h4>
          <i
            className={`fa-regular fa-circle-xmark fa-xl holdCursor ${css.closeIcon}`}
            onClick={() => setEditClient(null)}
          ></i>
        </div>

        <Formik
          initialValues={{
            companyName: editClient?.companyName || '',
            oktaDomain: editClient?.oktaDomain || '',
            oktaClientID: editClient?.oktaClientID || '',
            sfUsername: editClient?.sfUsername || '',
            sfSecurityToken: editClient?.sfSecurityToken || '',
            companyDomain: editClient?.companyDomain || '',
            sfPassword: editClient?.sfPassword || '',
            senderEmail: editClient?.senderEmail || '',
            status: editClient?.status === true ? 'active' : 'inactive'
          }}
          validationSchema={validationSchema}
          onSubmit={submitEditClient}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <div className='row'>
                <div className="col-md-6">
                  {/* Company Name */}
                  <div className="mb-3">
                    <label htmlFor="companyName" className="d-flex justify-start my-1 ms-3">
                      Company Name
                    </label>
                    <Field
                      type='text'
                      name="companyName"
                      placeholder="Company Name"
                      className={`${css.searchInput} inputStyle ${errors.companyName && touched.companyName ? 'border-danger' : ''}`}
                    />
                    {errors.companyName && touched.companyName && (
                      <ErrorMessage message={errors.companyName} />
                    )}
                  </div>

                  {/* Salesforce Username */}
                  <div className="mb-3">
                    <label htmlFor="sfUsername" className="d-flex justify-start my-1 ms-3">
                      Salesforce Username
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfUsername ? 'text' : 'password'}
                        name="sfUsername"
                        placeholder="Leave unchanged or enter new"
                        className={`${css.searchInput} inputStyle ${errors.sfUsername && touched.sfUsername ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfUsername')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfUsername ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfUsername && touched.sfUsername && (
                      <ErrorMessage message={errors.sfUsername} />
                    )}
                  </div>

                  {/* Salesforce Password */}
                  <div className="mb-3">
                    <label htmlFor="sfPassword" className="d-flex justify-start my-1 ms-3">
                      Salesforce Password
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfPassword ? 'text' : 'password'}
                        name="sfPassword"
                        placeholder="Leave unchanged or enter new"
                        className={`${css.searchInput} inputStyle ${errors.sfPassword && touched.sfPassword ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfPassword')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfPassword && touched.sfPassword && (
                      <ErrorMessage message={errors.sfPassword} />
                    )}
                  </div>

                  {/* Salesforce Security Token */}
                  <div className="mb-3">
                    <label htmlFor="sfSecurityToken" className="d-flex justify-start my-1 ms-3">
                      Salesforce Token
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.sfSecurityToken ? 'text' : 'password'}
                        name="sfSecurityToken"
                        placeholder="Leave unchanged or enter new"
                        className={`${css.searchInput} inputStyle ${errors.sfSecurityToken && touched.sfSecurityToken ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('sfSecurityToken')}
                      >
                        <i className={`fa-solid ${passwordVisibility.sfSecurityToken ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.sfSecurityToken && touched.sfSecurityToken && (
                      <ErrorMessage message={errors.sfSecurityToken} />
                    )}
                  </div>

                  {/* Company Domain */}
                  <div className="mb-3">
                    <label htmlFor="companyDomain" className="d-flex justify-start my-1 ms-3">
                      Company Domain
                    </label>
                    <Field
                      type='text'
                      name="companyDomain"
                      placeholder="example.com"
                      className={`${css.searchInput} inputStyle ${errors.companyDomain && touched.companyDomain ? 'border-danger' : ''}`}
                    />
                    {errors.companyDomain && touched.companyDomain && (
                      <ErrorMessage message={errors.companyDomain} />
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  {/* Sender Email */}
                  <div className="mb-3">
                    <label htmlFor="senderEmail" className="d-flex justify-start my-1 ms-3">
                      Sender Email
                    </label>
                    <Field
                      type="email"
                      name="senderEmail"
                      placeholder="sender@example.com"
                      className={`${css.searchInput} inputStyle ${errors.senderEmail && touched.senderEmail ? 'border-danger' : ''}`}
                    />
                    {errors.senderEmail && touched.senderEmail && (
                      <ErrorMessage message={errors.senderEmail} />
                    )}
                  </div>

                  {/* Okta Domain */}
                  <div className="mb-3">
                    <label htmlFor="oktaDomain" className="d-flex justify-start my-1 ms-3">
                      Okta Domain
                    </label>
                    <Field
                      type='text'
                      name="oktaDomain"
                      placeholder="https://your-domain.okta.com"
                      className={`${css.searchInput} inputStyle ${errors.oktaDomain && touched.oktaDomain ? 'border-danger' : ''}`}
                    />
                    {errors.oktaDomain && touched.oktaDomain && (
                      <ErrorMessage message={errors.oktaDomain} />
                    )}
                  </div>

                  {/* Okta Client ID */}
                  <div className="mb-3">
                    <label htmlFor="oktaClientID" className="d-flex justify-start my-1 ms-3">
                      Okta Client ID
                    </label>
                    <div className="position-relative">
                      <Field
                        type={passwordVisibility.oktaClientID ? 'text' : 'password'}
                        name="oktaClientID"
                        placeholder="Leave unchanged or enter new"
                        className={`${css.searchInput} inputStyle ${errors.oktaClientID && touched.oktaClientID ? 'border-danger' : ''}`}
                        style={{ paddingRight: '40px' }}
                      />
                      <div
                        className="position-absolute end-0 pe-3"
                        style={{
                          cursor: 'pointer',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          zIndex: 10
                        }}
                        onClick={() => toggleVisibility('oktaClientID')}
                      >
                        <i className={`fa-solid ${passwordVisibility.oktaClientID ? 'fa-eye-slash' : 'fa-eye'}`} />
                      </div>
                    </div>
                    {errors.oktaClientID && touched.oktaClientID && (
                      <ErrorMessage message={errors.oktaClientID} />
                    )}
                  </div>

                  {/* Logo Upload - WEBP Only */}
                  <div className="mb-3">
                    <label htmlFor="logo" className="d-flex justify-start my-1 ms-3">
                      Company Logo (WEBP only) {currentFileUrl && "(Upload new to replace)"}
                    </label>

                    {/* Current Logo Preview */}
                    {currentFileUrl && !selectedFile && (
                      <div className="mb-2 p-2 border rounded bg-light">
                        <div className="d-flex align-items-center">
                          <img
                            src={currentFileUrl}
                            alt="Current logo"
                            style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                            className="me-2"
                          />
                          <small className="text-muted">Current logo</small>
                        </div>
                      </div>
                    )}

                    {/* File Upload Input */}
                    <input
                      type='file'
                      name="logo"
                      accept=".webp,image/webp"
                      onChange={handleFileChange}
                      className={`${css.searchInput} inputStyle ${fileError ? 'border-danger' : ''}`}
                    />

                    {selectedFile && !fileError && (
                      <div className="text-success text-start ms-1 mt-1" style={{ fontSize: '0.875rem' }}>
                        <i className="fa-solid fa-check-circle me-1"></i>
                        New logo selected: {selectedFile.name} (old logo will be deleted)
                      </div>
                    )}
                    {fileError && (
                      <ErrorMessage message={fileError} />
                    )}
                  </div>

                  {/* Status */}
                  <div className="mb-3">
                    <label htmlFor="status" className="d-flex justify-start my-1 ms-3">
                      Status
                    </label>
                    <Field
                      as="select"
                      name="status"
                      className={`${css.searchInput} inputStyle ${errors.status && touched.status ? 'border-danger' : ''}`}
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Field>
                    {errors.status && touched.status && (
                      <ErrorMessage message={errors.status} />
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadStage && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <small className="text-muted">{uploadStage}</small>
                    <small className="text-muted fw-bold">{uploadProgress}%</small>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                      role="progressbar"
                      style={{ width: `${uploadProgress}%` }}
                      aria-valuenow={uploadProgress}
                      aria-valuemin="0"
                      aria-valuemax="100"
                    ></div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className={`${css.addButton} mb-1 w-100 d-flex justify-content-center align-items-center`}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating Client...' : 'Update Client Information'}
                {isSubmitting && <i className="ms-3 fas fa-spinner fa-pulse"></i>}
              </button>

              {/* Error Message */}
              {error && (
                <div className="alert alert-danger mt-3 mb-0 d-flex align-items-center" role="alert">
                  <i className="fa-solid fa-triangle-exclamation me-2"></i>
                  <span>{error}</span>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </section>
  );
}